<template>
  <q-card v-if="settings !== null" flat style="width: 700px; max-width: 70vw">
    <q-card-section>
      <div class="text-h6">Customize your recommendations</div>
      <div class="q-mt-xs text-grey-7">
        These controls change how HelloData calculates the recommended rent for this property. Try
        them out to see how they affect the recommended rent.
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <q-select
        v-model="includedProperties"
        transition-duration="100"
        :options="includedPropertiesOptions"
        label="Included Properties"
        hint="The properties to include in the recommended rent calculation."
        outlined
        dense
      />
      <q-select
        v-model="listingStatus"
        transition-duration="100"
        :options="listingStatusOptions"
        label="Listing Status"
        hint="The listing status of the units to include in the recommended rent calculation."
        outlined
        dense
        class="q-mt-md"
      />
      <date-range-picker
        title="Date Availability"
        :date-range="dateRange"
        btn-label="Listings last seen date"
        class="q-mt-md"
        @update="updateDateRange"
      />
      <div class="q-mt-lg">
        <q-toggle
          v-model="settings.exclude_subject"
          label="Exclude Subject"
          hint="Exclude units belonging to the subject property from the recommended rent calculation."
          outlined
          dense
        />
      </div>
    </q-card-section>
    <q-separator />
    <q-card-actions align="right">
      <q-btn v-close-popup unelevated label="Cancel" no-caps />
      <q-btn v-close-popup unelevated label="Recalculate" color="primary" no-caps @click="save" />
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { format, addDays, parseISO } from 'date-fns'
import { ListingStatus, type PricingSettings } from 'common'
import DateRangePicker from '@/components/DateRangePicker.vue'

export default {
  name: 'CardPricingSettings',
  components: {
    DateRangePicker
  },
  props: {
    compsSettings: {
      type: Object as PropType<PricingSettings>,
      required: true
    },
    compsIds: {
      type: Array as PropType<string[]>,
      required: true
    }
  },
  emits: ['update:compsSettings'],
  data() {
    return {
      settings: null as PricingSettings | null,
      includedProperties: 'Selected Comps Only',
      includedPropertiesOptions: ['Recommended', 'Selected Comps Only'],
      listingStatus: 'All',
      listingStatusOptions: ['Closed', 'Active', 'All'],
      dateRange: { from: addDays(new Date(), -30), to: new Date() } as {
        from: Date
        to: Date
      } as { from: Date; to: Date }
    }
  },
  computed: {
    countExcludedUnits(): number {
      const settings = this.settings
      if (settings === null) {
        return 0
      }
      if (settings.excluded_unit_ids === null || settings.excluded_unit_ids === undefined) {
        return 0
      }
      return settings.excluded_unit_ids.length
    }
  },
  mounted() {
    this.settings = { ...this.compsSettings }

    if (this.settings.from_date && this.settings.to_date) {
      this.dateRange = {
        from: parseISO(this.settings.from_date),
        to: parseISO(this.settings.to_date)
      }
    }

    if (this.compsSettings.building_ids && this.compsSettings.building_ids.length > 0) {
      this.includedProperties = 'Selected Comps Only'
    } else {
      this.includedProperties = 'Recommended'
    }

    if (this.settings.listing_status === ListingStatus.CLOSED) {
      this.listingStatus = 'Closed'
    } else if (this.settings.listing_status === ListingStatus.ACTIVE) {
      this.listingStatus = 'Active'
    } else if (this.settings.listing_status === ListingStatus.ALL) {
      this.listingStatus = 'All'
    }
  },
  methods: {
    updateDateRange(dateRange: { from: Date; to: Date }) {
      this.dateRange = {
        from: dateRange.from,
        to: dateRange.to
      }
    },
    save() {
      if (this.settings) {
        if (this.listingStatus === 'Closed') {
          this.settings.listing_status = ListingStatus.CLOSED
        } else if (this.listingStatus === 'Active') {
          this.settings.listing_status = ListingStatus.ACTIVE
        } else if (this.listingStatus === 'All') {
          this.settings.listing_status = ListingStatus.ALL
        }

        this.settings.from_date = format(this.dateRange.from, 'yyyy-MM-dd')
        this.settings.to_date = format(this.dateRange.to, 'yyyy-MM-dd')

        if (this.includedProperties === 'Selected Comps Only') {
          this.settings.building_ids = this.compsIds
        } else {
          this.settings.building_ids = []
        }
        this.$tracker.changedPricingSettings(this.settings)
        this.$emit('update:compsSettings', this.settings)
      }
    }
  }
}
</script>
